import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { EnvelopeIcon, LockClosedIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import AuthShell from './AuthShell';
import { Spinner } from './ui';

/** Shown to signed-in people who cannot use the app (yet). */
const AccessGate: React.FC = () => {
  const { user, status, signOut, resendVerification, recheckAccess } = useAuth();
  const { t } = useLanguage();
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const email = user?.email || '';

  const handleRecheck = async () => {
    setChecking(true);
    try {
      await recheckAccess();
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setSending(true);
    try {
      await resendVerification();
      toast.success(t('access.verificationSent', { email }));
    } catch (error: any) {
      toast.error(error?.code === 'auth/too-many-requests' ? t('auth.errors.tooManyRequests') : t('common.genericError'));
    } finally {
      setSending(false);
    }
  };

  const content = {
    unverified: { icon: EnvelopeIcon, title: t('access.unverifiedTitle'), text: t('access.unverifiedText', { email }) },
    denied: { icon: LockClosedIcon, title: t('access.deniedTitle'), text: t('access.deniedText', { email }) },
    maintenance: { icon: WrenchScrewdriverIcon, title: t('access.maintenanceTitle'), text: t('access.maintenanceText') },
  }[status as 'unverified' | 'denied' | 'maintenance'] || {
    icon: LockClosedIcon,
    title: t('access.deniedTitle'),
    text: t('access.deniedText', { email }),
  };
  const Icon = content.icon;

  return (
    <AuthShell>
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 text-primary-700 dark:bg-primary-950 dark:text-primary-300">
        <Icon className="h-5 w-5" />
      </div>
      <h1 className="mt-5 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{content.title}</h1>
      <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{content.text}</p>

      <div className="mt-7 space-y-2">
        {status === 'unverified' && (
          <>
            <button type="button" onClick={handleRecheck} disabled={checking} className="btn btn-primary w-full">
              {checking && <Spinner className="h-4 w-4" />}
              {t('access.iVerified')}
            </button>
            <button type="button" onClick={handleResend} disabled={sending} className="btn btn-secondary w-full">
              {t('access.resend')}
            </button>
          </>
        )}
        {status === 'denied' && (
          <button type="button" onClick={handleRecheck} disabled={checking} className="btn btn-secondary w-full">
            {checking && <Spinner className="h-4 w-4" />}
            {t('access.checkAgain')}
          </button>
        )}
        <button type="button" onClick={signOut} className="btn btn-ghost w-full">
          {t('access.useOtherAccount')}
        </button>
      </div>
    </AuthShell>
  );
};

export default AccessGate;
